<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12 full-height-page-wrapper">
        <div class="page-table-header">
          <PageHeader showBreadcrumbs page-title="Bank details">
            <template #buttons>
              <b-button
                class="button title-container__controls-item"
                :variant="isShowFilter ? 'info' : 'secondary'"
                @click="toggleShowFilter"
              >
                <span class="material-icons icon-image-preview">
                  tune
                </span>
                Filters
              </b-button>
              <b-button
                v-if="$can(permissions.CreateFinancesOwnBanDetails)"
                variant="success"
                class="button"
                @click.prevent="showCreateModal"
              >
                Create
              </b-button>
            </template>
          </PageHeader>

          <Filters
            ref="filtersComponent"
            v-show="isShowFilter"
            textButtonSubmit="Filter"
            :data="getFilters"
            :isLoading="isDataPageLoading && isInitialDataLoaded"
            @submit="setFilterBankDetails"
          />
        </div>

        <div v-if="isDataPageLoading && !isInitialDataLoaded" class="content-loading-wrapper">
          <Preloader class="centered-preloader" />
        </div>

        <div v-else class="full-height-content">
          <TableForPage
            v-if="getBankDetailsTableData && getBankDetailsTableData.length"
            ref="table"
            :tableData="getBankDetailsTableData || []"
            :isDataLoading="isDataPageLoading && isInitialDataLoaded"
            :fields="getTableFields"
            :perPage="getBankDetails.per_page"
            :currentPage="getBankDetails.current_page"
            :totalRows="getBankDetails.total"
            :tableRowModalFields="tableRowModal.fields"
            :isEdit="$can(permissions.UpdateAllFinancesOwnBankDetails)"
            :isRemove="$can(permissions.DeleteFinancesOwnBankDetails)"
            isShowView
            isServerRender
            noLocalSorting
            @edit="showEditModal"
            @remove="removeBankDetails"
            @update="updateBankDetails"
          />
          <EmptyStateNoResults
            v-else-if="isInitialDataLoaded && getBankDetailsTableData && !getBankDetailsTableData.length && !getBankDetails.isAnyData"
            customIcon="account_balance"
            customIconClasses="material-symbols-outlined empty-state__icon"
            title="No financial accounts added"
            description="You haven't added any financial accounts. Create one to start tracking transactions and balances."
            buttonText="CREATE"
            @button-click="handleCreateClick"
          />
          <EmptyStateNoResults
            v-else-if="isInitialDataLoaded && getBankDetailsTableData && !getBankDetailsTableData.length && getBankDetails.isAnyData"
            @reset-filters="handleResetFilters"
          />
        </div>
      </div>
    </div>

    <b-modal
      size="lg"
      hide-footer
      v-model="createModal.show"
      :title="createModal.title"
      centered
      :modal-class="isCreateModalLoading ? 'loading' : ''"
    >
      <FormGenerator
        :data="createModal.form"
        :isLoading="isCreateModalLoading"
        @submit="createBankDetails"
        :showReset="false"
        :textButtonSubmit="createModal.submitButtonText"
      >
        <template v-slot:companyName="slotProps">
          <b-form-group v-if="isBusinessEntityCompany(slotProps.fields.business_entity_type)">
            <label>{{slotProps.data.label}}</label>
            <b-form-input
              v-model="slotProps.data.value"
              :required="slotProps.data.required"
              :placeholder="slotProps.data.placeholder"
            />
          </b-form-group>
        </template>
        <template v-slot:companyNameNational="slotProps">
          <b-form-group v-if="isBusinessEntityCompany(slotProps.fields.business_entity_type)">
            <label>{{slotProps.data.label}}</label>
            <b-form-input
              v-model="slotProps.data.value"
              :required="slotProps.data.required"
              :placeholder="slotProps.data.placeholder"
            />
          </b-form-group>
        </template>
      </FormGenerator>
    </b-modal>
    <b-modal
      size="lg"
      hide-footer
      v-model="editModal.show"
      :title="editModal.title"
      centered
      :modal-class="isEditModalLoading ? 'loading' : ''"
    >
      <FormGenerator
        :data="editModal.form"
        :isLoading="isEditModalLoading"
        @submit="editBankDetails"
        :showReset="false"
        :textButtonSubmit="editModal.submitButtonText"
      >
        <template v-slot:companyName="slotProps">
          <b-form-group v-if="isBusinessEntityCompany(slotProps.fields.business_entity_type)">
            <label>{{slotProps.data.label}}</label>
            <b-form-input
              v-model="slotProps.data.value"
              :required="slotProps.data.required"
              :placeholder="slotProps.data.placeholder"
            />
          </b-form-group>
        </template>
        <template v-slot:companyNameNational="slotProps">
          <b-form-group v-if="isBusinessEntityCompany(slotProps.fields.business_entity_type)">
            <label>{{slotProps.data.label}}</label>
            <b-form-input
              v-model="slotProps.data.value"
              :required="slotProps.data.required"
              :placeholder="slotProps.data.placeholder"
            />
          </b-form-group>
        </template>
      </FormGenerator>
    </b-modal>
  </div>
</template>

<script>
import TableForPage from '@tables/TableForPage'
import Filters from '@components/Filter/Filter'
import FormGenerator from '@components/FormGenerator'
import PageHeader from '@components/PageHeader'
import BankDetailsMixin from '../mixins/BankDetailsMixin'
import Preloader from '@components/Preloader'
import EmptyStateNoResults from '@components/EmptyStateNoResults'

export default {
  name: 'FinanceBankDetails',
  components: {
    TableForPage,
    Filters,
    FormGenerator,
    PageHeader,
    Preloader,
    EmptyStateNoResults
  },
  mixins: [BankDetailsMixin],
  data () {
    return {
      modalTitle: 'Own bank details',
      ownershipId: 1,
      isCreateModalLoading: false,
      isEditModalLoading: false,
      isInitialDataLoaded: false,
      tableRowModal: {
        fields: [
          {
            key: 'name',
            label: 'Name'
          },
          {
            key: 'address',
            label: 'Address'
          },
          {
            key: 'sign.owner',
            label: 'Sign'
          },
          {
            key: 'address_national',
            label: 'Address national'
          },
          {
            key: 'bank_address',
            label: 'Bank address'
          },
          {
            key: 'bank_address',
            label: 'Bank address'
          },
          {
            key: 'bank_correspondent_address',
            label: 'Bank correspondent address'
          },
          {
            key: 'bank_correspondent_account',
            label: 'Bank correspondent account'
          },
          {
            key: 'bank_correspondent_iban',
            label: 'Bank correspondent Iban'
          },
          {
            key: 'bank_correspondent_mfi',
            label: 'Bank correspondent MFI'
          },
          {
            key: 'bank_correspondent_name',
            label: 'Bank correspondent name'
          },
          {
            key: 'bank_correspondent_swift',
            label: 'Bank correspondent Swift'
          },
          {
            key: 'bank_mfi',
            label: 'Bank MFI'
          },
          {
            key: 'bank_name',
            label: 'Bank name'
          },
          {
            key: 'business_entity_type.type',
            label: 'Business entity type'
          },
          {
            key: 'city',
            label: 'City'
          },
          {
            key: 'city_national',
            label: 'City national'
          },
          {
            key: 'company_name',
            label: 'Company name'
          },
          {
            key: 'company_name_national',
            label: 'Company name national'
          },
          {
            key: 'country.name',
            label: 'Country'
          },
          {
            key: 'currency.code',
            label: 'Currency'
          },
          {
            key: 'email',
            label: 'Email'
          },
          {
            key: 'extract_from_unified_register',
            label: 'Extract from unified register'
          },
          {
            key: 'first_name',
            label: 'First name'
          },
          {
            key: 'last_name',
            label: 'Last name'
          },
          {
            key: 'last_name_national',
            label: 'Last name national'
          },
          {
            key: 'first_name_national',
            label: 'First name national'
          },
          {
            key: 'iban',
            label: 'Iban'
          },
          {
            key: 'phone',
            label: 'Phone'
          },
          {
            key: 'second_name_national',
            label: 'Second name national'
          },
          {
            key: 'summary',
            label: 'Summary'
          },
          {
            key: 'swift',
            label: 'Swift'
          },
          {
            key: 'tax_registration_number',
            label: 'Tax registration number'
          },
          {
            key: 'zip_code',
            label: 'Zip code'
          }
        ]
      },
      currentFields: [
        'name',
        'currency.code',
        'country.name',
        'business_entity_type.type',
        'first_name',
        'last_name',
        'company_name',
        'sign.owner',
        'actions'
      ]
    }
  },
  computed: {
    getFilters () {
      const currentFilers = (({
        currencyId,
        country,
        businessEntityTypeId,
        firstName,
        lastName,
        companyName,
        sign
      }) => ({
        currencyId,
        country,
        businessEntityTypeId,
        firstName,
        lastName,
        companyName,
        sign
      }))(this.filters)

      return currentFilers
    },
    formMock () {
      return {
        'id': {
          name: 'id'
        },
        'sign': {
          type: 'select',
          name: 'sign_id',
          label: 'Sign',
          selectLabel: 'owner',
          valueField: 'id',
          options: [],
          value: null,
          class: 'col-12'
        },
        ...this.mainFormMock
      }
    }
  },
  methods: {
    fetchAllData () {
      this.fetchBankDetails()
      Promise.all([
        this.fetchBusinessEntityTypes(),
        this.fetchCountries(),
        this.fetchCurrencies(),
        this.fetchBankDetailsSigns()
      ])
        .then(values => {
          this.setAllOptionsFilter()
        })
    },
    async fetchBankDetailsSigns () {
      if (this.getBankDetailsSigns.length) return

      return this.$api.finances.getBankDetailsSigns()
    },
    setAllOptionsFilter () {
      this.createModal.form.business_entity_type.options = this.getBusinessEntityTypes
      this.createModal.form.country.options = this.getCountries
      this.createModal.form.currency.options = this.getCurrencies
      this.createModal.form.sign.options = this.getBankDetailsSigns

      this.editModal.form.business_entity_type.options = this.getBusinessEntityTypes
      this.editModal.form.country.options = this.getCountries
      this.editModal.form.currency.options = this.getCurrencies
      this.editModal.form.sign.options = this.getBankDetailsSigns

      this.filters.country.options = this.getCountries
      this.filters.businessEntityTypeId.options = this.getBusinessEntityTypes
      this.filters.currencyId.options = this.getCurrencies
      this.filters.sign.options = this.getBankDetailsSigns

      this.isDataPageLoading = false
      this.isInitialDataLoaded = true
    },
    handleResetFilters () {
      if (this.$refs.filtersComponent) {
        this.$refs.filtersComponent.resetFilter()
      }
    },
    handleCreateClick () {
      this.showCreateModal()
    },
    async createBankDetails (fields) {
      const data = this.prepareDataForEditAndCreateRequest(fields)
      data.ownership_id = this.ownershipId

      this.isCreateModalLoading = true
      try {
        await this.$api.finances.createBankDetails(data)
        this.$snotify.success(
          'Bank details created successfully',
          'Success',
          this.$store.state.snotify
        )
        this.hideCreateModal()
        this.updateBankDetails()
      } catch (error) {
        this.errorNotifications(error)
      } finally {
        this.isCreateModalLoading = false
      }
    },
    async editBankDetails (fields) {
      const data = this.prepareDataForEditAndCreateRequest(fields)
      data.ownership_id = this.ownershipId

      this.isEditModalLoading = true
      try {
        await this.$api.finances.editBankDetails(data)
        this.$snotify.success(
          'Bank details updated successfully',
          'Success',
          this.$store.state.snotify
        )
        this.hideEditModal()
        this.updateBankDetails()
      } catch (error) {
        this.errorNotifications(error)
      } finally {
        this.isEditModalLoading = false
      }
    }
  }
}
</script>
